import React, { useEffect, useState } from 'react';
import {
  Box, Tabs, Tab, useTheme} from '@mui/material';
import BreakdownFormDialog from '../EquipmentBreakdown/BreakdownDialog';
import BreakdownTable from '../EquipmentBreakdown/BreakdownTable';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { Build } from '@mui/icons-material';
import { BreakdownCategory, BreakdownSeverity, Meta } from '../../../types';
import { equipmentApi } from '../../../services/api';
import { extractApiError, unwrapListResponse, unwrapMetaResponse } from '../../../apiUnwrapper/apiUnwrapper';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import {
  PageHeader,
} from '../../../components/SharedPageParts';
import { useAuth } from '../../../services/AuthContext';

const EquipmentBreakdownPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageBreakdown = user?.roleName === 'Admin';
  const [tabIndex, setTabIndex] = useState(0);

  const [categories, setCategories] = useState<BreakdownCategory[]>([]);
  const [severities, setSeverities] = useState<BreakdownSeverity[]>([]);

  const [selectedRow, setSelectedRow] = useState<any | undefined>(undefined);
  const [isEditMode, setIsEditMode] = useState(false);
  const [dialogType, setDialogType] = useState<"CATEGORY" | "SEVERITY">("CATEGORY");
  const [openDialog, setOpenDialog] = useState(false);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
  const [confirmMessage, setConfirmMessage] = useState("Are you sure?");
  const [categoryMeta, setCategoryMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });
  const [severityMeta, setSeverityMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });

  useEffect(() => {
    loadCategories();
    loadSeverities();
  }, []);

  const loadCategories = async (page = 1, size = 5) => {
    const data = await equipmentApi.getBreakdownCategories(page, size);
    setCategories(unwrapListResponse<BreakdownCategory>(data));
    setCategoryMeta(unwrapMetaResponse<Meta>(data) || { total: 0, size: 5, page: 1 });
  };

  const loadSeverities = async (page = 1, size = 5) => {
    const data = await equipmentApi.getBreakdownSeverity(page, size);
    setSeverities(unwrapListResponse<BreakdownSeverity>(data));
    setSeverityMeta(unwrapMetaResponse<Meta>(data) || { total: 0, size: 5, page: 1 });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleSubmit = async (data: any) => {
    try{
        if (dialogType === 'CATEGORY') {
          await equipmentApi.createUpdateBreakdownCategory(data);
          loadCategories(categoryMeta.page, categoryMeta.size);
        } else {
          await equipmentApi.createUpdateBreakdownSeverity(data);
          loadSeverities(severityMeta.page, severityMeta.size);
        }
        setOpenDialog(false);
        setSelectedRow(undefined);
    }catch(error){
      const errorMessage = extractApiError(error);
      enqueueSnackbar(
          dialogType === 'CATEGORY'
            ? `Failed to create/update Category! - ${errorMessage.message}`
            : `Failed to create/update severity! - ${errorMessage.message}`,
          { variant: "error" }
        );
    }
  };

  const handleDelete = (type: "CATEGORY" | "SEVERITY", id: number) => {
    setConfirmAction(() => async () => {
      if (type === 'CATEGORY') {
        await equipmentApi.deleteBreakdownCategory(id);
        loadCategories(categoryMeta.page, categoryMeta.size);
      } else {
        await equipmentApi.deleteBreakdownSeverity(id);
        loadSeverities(severityMeta.page, severityMeta.size);
      }
    });
    setConfirmMessage(`Are you sure you want to delete this ${type.toLowerCase()}?`);
    setOpenConfirm(true);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto' }}>
      <PageHeader
        title="Equipment Breakdown"
        Icon={Build}
        onBack={() => navigate('/prechecksandforms')}
        backLabel="Back to Pre Checks and Forms"
        subtitle="Category and Severity configuration"
      />
      
      <Tabs value={tabIndex} onChange={handleTabChange} textColor="inherit" indicatorColor="secondary"
        sx={{
            mt: 2, mr:2, ml:2,
            display: 'flex',
            backgroundColor: theme.palette.background.default
        }}>
        <Tab label="Categories" sx={{
            flex: 1,
            color: theme.palette.text.primary,
            textTransform: 'none',
            fontWeight: 'normal',
            '&.Mui-selected': {
                background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                fontWeight: 'bold',
                borderRadius: 1
            },
            '&.Mui-focusVisible': {
                outline: '2px solid #667eea',
                outlineOffset: '2px'
            },
            '&:hover': {
                background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)'
            }
            }} />
        <Tab label="Severities" sx={{
            flex: 1,
            color: theme.palette.text.primary,
            textTransform: 'none',
            fontWeight: 'normal',
            '&.Mui-selected': {
                background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                fontWeight: 'bold',
                borderRadius: 1
            },
            '&.Mui-focusVisible': {
                outline: '2px solid #667eea',
                outlineOffset: '2px'
            },
            '&:hover': {
                background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)'
            }
            }} />
      </Tabs>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={confirmAction}
        title="Confirm Delete"
        message={confirmMessage}
      />

      {tabIndex === 0 && (
        <BreakdownTable
          title={`Category`}
          rows={categories}
          canManage={canManageBreakdown}
          onAdd={() => { setSelectedRow(undefined); setIsEditMode(false); setDialogType('CATEGORY'); setOpenDialog(true); }}
          onEdit={(row) => { setSelectedRow(row); setIsEditMode(true); setDialogType('CATEGORY'); setOpenDialog(true); }}
          onDelete={(id) => handleDelete('CATEGORY', id)}
          tableMeta={categoryMeta}
          loadQuestions={(page, size) => loadCategories(page, size)}
        />
      )}

      {tabIndex === 1 && (
        <BreakdownTable
          title={`Severity`}
          rows={severities}
          canManage={canManageBreakdown}
          onAdd={() => { setSelectedRow(undefined); setIsEditMode(false); setDialogType('SEVERITY'); setOpenDialog(true); }}
          onEdit={(row) => { setSelectedRow(row); setIsEditMode(true); setDialogType('SEVERITY'); setOpenDialog(true); }}
          onDelete={(id) => handleDelete('SEVERITY', id)}
          tableMeta={severityMeta}
          loadQuestions={(page, size) => loadSeverities(page, size)}
        />
      )}

      {canManageBreakdown && <BreakdownFormDialog
        open={openDialog}
        onClose={() => 
          {
            setSelectedRow(undefined);
            setOpenDialog(false);
          }}
        onSubmit={handleSubmit}
        row={selectedRow}
        type={dialogType}
      />}
      </Box>
    </Box>
  );
};

export default EquipmentBreakdownPage;